import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/LoadingSpinner';
import DOMPurify from 'dompurify';
import Swal from 'sweetalert2';
import i18n from '../i18n';
import { Link, useNavigate } from 'react-router-dom';
import '../css/news.css'; // Changed from patch-notes.css to news.css
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faClock, faUser } from "@fortawesome/free-regular-svg-icons";
import { faArrowRight, faBookOpen, faTrash, faEdit, faTag } from "@fortawesome/free-solid-svg-icons";
import RichTextEditor from './RichTextEditor';
import ReactPaginate from "react-paginate";
import api from "../api";


const quillModules = {};

const quillFormats = [];

const backgrounds = [
    '/images/news-defaults/gatling.png',
    '/images/news-defaults/driver.png',
    '/images/news-defaults/firearms_expert.png',
    '/images/news-defaults/gas_soldier.png',
    '/images/news-defaults/marine.png',
    '/images/news-defaults/nuclear.png',
    '/images/news-defaults/seeker.png',
    '/images/news-defaults/shotgun_master.png',
    '/images/news-defaults/sniper.png',
    '/images/news-defaults/strike_force.png',
    '/images/news-defaults/swat.png',
    '/images/news-defaults/teleporter.png',
    '/images/news-defaults/titan.png'
];

const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

function PatchNotesTab({ loadingBarRef }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('createPatchNotes');
    const [language, setLanguage] = useState(i18n.language);
    const [createPatchNotesData, setCreatePatchNotesData] = useState({
        date: getCurrentDateTime(),
        title: { en: '', ru: '', ua: '', ko: '', fr: '' },
        content: { en: '', ru: '', ua: '', ko: '', fr: '' },
        version: ''
    });
    const [editPatchNotesData, setEditPatchNotesData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [patchNotesList, setPatchNotesList] = useState([]);
    const [selectedPatchNotesLang, setSelectedPatchNotesLang] = useState(i18n.language);
    const [showPreview, setShowPreview] = useState(false);
    const [showEditPreview, setShowEditPreview] = useState(false);
    const [media, setMedia] = useState(null);
    const [fileName, setFileName] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedPatchNotes, setSelectedPatchNotes] = useState(null);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const limit = 10;
    const [importing, setImporting] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [draftsLoading, setDraftsLoading] = useState(false);
    const [isDraftEdit, setIsDraftEdit] = useState(false);

    useEffect(() => {
        if (media) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewImageUrl(e.target.result);
            };
            reader.readAsDataURL(media);
        } else {
            setPreviewImageUrl(null);
        }
    }, [media]);

    const handleDateChange = (e) => {
        if (activeTab === 'createPatchNotes') {
            setCreatePatchNotesData({ ...createPatchNotesData, date: e.target.value });
        } else if (isEditing) {
            setEditPatchNotesData({ ...editPatchNotesData, date: e.target.value });
        }
    };

    const handleVersionChange = (e) => {
        if (activeTab === 'createPatchNotes') {
            setCreatePatchNotesData({ ...createPatchNotesData, version: e.target.value });
        } else if (isEditing) {
            setEditPatchNotesData({ ...editPatchNotesData, version: e.target.value });
        }
    };

    const handleChange = (field, value, lang) => {
        if (activeTab === 'createPatchNotes') {
            setCreatePatchNotesData((prevData) => ({
                ...prevData,
                [field]: {
                    ...prevData[field],
                    [lang]: value
                }
            }));
        } else if (isEditing) {
            setEditPatchNotesData((prevData) => ({
                ...prevData,
                [field]: {
                    ...prevData[field],
                    [lang]: value
                }
            }));
        }
    };

    const validatePatchNotesFields = (patchNotesData) => {
        const errors = [];
        for (const lang of ['en', 'ru', 'ua', 'ko', 'fr']) {
            if (!patchNotesData.title[lang].trim()) {
                errors.push(t('patchNotes.validationError.title', { lang: t(`patchNotes.languages.${lang}`) }));
            }
            if (!patchNotesData.content[lang].trim()) {
                errors.push(t('patchNotes.validationError.content', { lang: t(`patchNotes.languages.${lang}`) }));
            }
        }
        return errors;
    };

    const confirmSubmitPatchNotes = (e) => {
        e.preventDefault();
        const validationErrors = validatePatchNotesFields(createPatchNotesData);
        if (validationErrors.length > 0) {
            Swal.fire({
                title: t('patchNotes.validationError.titleHeader'),
                html: `<ul>${validationErrors.map(err => `<li>${err}</li>`).join('')}</ul>`,
                icon: 'error',
                confirmButtonText: t('posts.ok'),
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button',
                }
            });
            return;
        }
        Swal.fire({
            text: t('patchNotes.confirmSubmit'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                submitPatchNotes(false);
            }
        });
    };

    const confirmEditPatchNotes = (e) => {
        e.preventDefault();
        const validationErrors = validatePatchNotesFields(editPatchNotesData);
        if (validationErrors.length > 0) {
            Swal.fire({
                title: t('patchNotes.validationError.titleHeader'),
                html: `<ul>${validationErrors.map(err => `<li>${err}</li>`).join('')}</ul>`,
                icon: 'error',
                confirmButtonText: t('posts.ok'),
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button',
                }
            });
            return;
        }
        Swal.fire({
            text: t('patchNotes.confirmEdit'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleEditPatchNotes();
            }
        });
    };

    const submitPatchNotes = async (draft = false) => {
        setIsSubmitting(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        const sanitizedContent = {
            en: DOMPurify.sanitize(createPatchNotesData.content.en),
            ru: DOMPurify.sanitize(createPatchNotesData.content.ru),
            ua: DOMPurify.sanitize(createPatchNotesData.content.ua),
            ko: DOMPurify.sanitize(createPatchNotesData.content.ko),
            fr: DOMPurify.sanitize(createPatchNotesData.content.fr),
        };
        const sanitizedPatchNotes = { ...createPatchNotesData, content: sanitizedContent, draft };
        const formData = new FormData();
        if (media) {
            formData.append('image', media);
        }
        formData.append('patchNotesData', JSON.stringify(sanitizedPatchNotes));
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/patch-notes`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${accessToken}` },
                body: formData
            });
            if (response.ok) {
                Swal.fire({
                    text: draft ? t('patchNotes.draftSaved') : t('patchNotes.success'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                setCreatePatchNotesData({
                    date: getCurrentDateTime(),
                    title: { en: '', ru: '', ua: '', ko: '', fr: '' },
                    content: { en: '', ru: '', ua: '', ko: '', fr: '' },
                    version: ''
                });
                setShowPreview(false);
                setMedia(null);
                setFileName('');
            } else {
                throw new Error('Failed to submit patch notes');
            }
        } catch (error) {
            Swal.fire({
                text: draft ? t('patchNotes.draftError') : t('patchNotes.error'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setIsSubmitting(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const handleSaveDraft = () => {
        if (!createPatchNotesData.title.en.trim()) {
            Swal.fire({
                text: t('patchNotes.validationError.title', { lang: t('patchNotes.languages.en') }),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }
        submitPatchNotes(true);
    };

    const handleTranslate = async () => {
        if (!editPatchNotesData) return;
        setTranslating(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/patch-notes/translate`, {
                title: editPatchNotesData.title.en,
                body: editPatchNotesData.content.en
            });
            const translations = response.data || {};
            setEditPatchNotesData((prev) => {
                const title = { ...prev.title };
                const content = { ...prev.content };
                ['ru', 'ua', 'ko', 'fr'].forEach((lang) => {
                    if (translations[lang]) {
                        title[lang] = translations[lang].title || '';
                        content[lang] = translations[lang].body || '';
                    }
                });
                return { ...prev, title, content };
            });
        } catch (error) {
            Swal.fire({
                text: t('patchNotes.translateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setTranslating(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const handleTranslateCreate = async () => {
        if (!createPatchNotesData.title.en.trim()) {
            Swal.fire({
                text: t('patchNotes.validationError.title', { lang: t('patchNotes.languages.en') }),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }
        setTranslating(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/patch-notes/translate`, {
                title: createPatchNotesData.title.en,
                body: createPatchNotesData.content.en
            });
            const translations = response.data || {};
            setCreatePatchNotesData((prev) => {
                const title = { ...prev.title };
                const content = { ...prev.content };
                ['ru', 'ua', 'ko', 'fr'].forEach((lang) => {
                    if (translations[lang]) {
                        title[lang] = translations[lang].title || '';
                        content[lang] = translations[lang].body || '';
                    }
                });
                return { ...prev, title, content };
            });
        } catch (error) {
            Swal.fire({
                text: t('patchNotes.translateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setTranslating(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const fetchPatchNotesList = useCallback(async () => {
        setIsLoading(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.get(
                `${process.env.REACT_APP_API_URL}/api/patch-notes?lang=${selectedPatchNotesLang}&page=${page}&limit=${limit}`,
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                }
            );
            setPatchNotesList(response.data.patch_notes);
            setTotalPages(Math.ceil(response.data.total / limit));
        } catch (error) {
            console.error('Error fetching patch notes:', error);
        } finally {
            setIsLoading(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    }, [loadingBarRef, selectedPatchNotesLang, page, limit]);

    useEffect(() => {
        if (activeTab === 'managePatchNotes') {
            setPage(1);
        }
    }, [activeTab, selectedPatchNotesLang]);

    const handleSelectPatchNotes = async (groupId) => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/patch-notes/group/${groupId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
            });
            const patchNotesGroup = response.data;
            const titles = { en: '', ru: '', ua: '', ko: '', fr: '' };
            const contents = { en: '', ru: '', ua: '', ko: '', fr: '' };
            let version = '';
            
            patchNotesGroup.forEach(patchNotesItem => {
                const lang = patchNotesItem.lang;
                if (lang && titles.hasOwnProperty(lang)) {
                    titles[lang] = patchNotesItem.title;
                    contents[lang] = patchNotesItem.description;
                    if (!version && patchNotesItem.version) {
                        version = patchNotesItem.version;
                    }
                }
            });
            
            const formattedDate = formatDateForInput(patchNotesGroup[0].createdAt.date || patchNotesGroup[0].created_at);
            setEditPatchNotesData({
                date: formattedDate,
                title: titles,
                content: contents,
                version: version
            });
            setSelectedGroupId(groupId);
            setSelectedPatchNotes(patchNotesGroup[0]);
            setIsDraftEdit(false);
            setIsEditing(true);
            setFileName(patchNotesGroup[0].image ? patchNotesGroup[0].image.split('/').pop() : '');
            setShowEditPreview(false);
        } catch (error) {
            Swal.fire({
                text: t('patchNotes.fetchError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        }
    };

    const handleEditPatchNotes = async () => {
        setIsSubmitting(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        const sanitizedContent = {
            en: DOMPurify.sanitize(editPatchNotesData.content.en),
            ru: DOMPurify.sanitize(editPatchNotesData.content.ru),
            ua: DOMPurify.sanitize(editPatchNotesData.content.ua),
            ko: DOMPurify.sanitize(editPatchNotesData.content.ko),
            fr: DOMPurify.sanitize(editPatchNotesData.content.fr),
        };
        const sanitizedPatchNotes = { ...editPatchNotesData, content: sanitizedContent };
        const formData = new FormData();
        if (media) {
            formData.append('image', media);
        }
        const patchNotesData = {
            date: sanitizedPatchNotes.date,
            titles: sanitizedPatchNotes.title,
            contents: sanitizedPatchNotes.content,
            version: sanitizedPatchNotes.version
        };
        formData.append('patchNotesData', JSON.stringify(patchNotesData));
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/patch-notes/group/${selectedGroupId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${accessToken}` },
                body: formData
            });
            if (response.ok) {
                Swal.fire({
                    text: t('patchNotes.updateSuccess'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            } else {
                throw new Error('Failed to update patch notes');
            }
            fetchPatchNotesList();
            setIsEditing(false);
            setSelectedGroupId(null);
            setSelectedPatchNotes(null);
            setEditPatchNotesData(null);
            setMedia(null);
            setFileName('');
            setShowEditPreview(false);
        } catch (error) {
            Swal.fire({
                text: t('patchNotes.updateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setIsSubmitting(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    useEffect(() => {
        setLanguage(i18n.language);
        setSelectedPatchNotesLang(i18n.language);
    }, [i18n.language]);

    useEffect(() => {
        if (activeTab === 'managePatchNotes') {
            fetchPatchNotesList();
        }
    }, [activeTab, selectedPatchNotesLang, fetchPatchNotesList]);

    const confirmDeletePatchNotes = async (groupId) => {
        Swal.fire({
            text: t('patchNotes.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                await handleDeletePatchNotes(groupId);
            }
        });
    };

    const handleDeletePatchNotes = async (groupId) => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const accessToken = localStorage.getItem('access_token');
            await api.delete(`${process.env.REACT_APP_API_URL}/api/admin/patch-notes/${groupId}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            Swal.fire({
                text: t('patchNotes.deleteSuccess'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            setPatchNotesList(patchNotesList.filter(patchNotes => patchNotes.group_id !== groupId));
        } catch (error) {
            Swal.fire({
                text: t('patchNotes.deleteError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const getRandomBackgrounds = () => {
        const shuffledBackgrounds = [...backgrounds].sort(() => 0.5 - Math.random());
        return shuffledBackgrounds.slice(0, patchNotesList.length);
    };

    const randomBackgrounds = getRandomBackgrounds();

    const handlePreview = () => {
        setShowPreview(true);
    };

    const handleBackToEditing = () => {
        setShowPreview(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setMedia(file);
            setFileName(file.name);
        }
    };

    const handlePageClick = (event) => {
        setPage(event.selected + 1);
    };

    const handleImport = async () => {
        setImporting(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/patch-notes/import`);
            const { added, skipped, errors } = response.data;
            let text = t('patchNotes.importSuccess', { added, skipped });
            if (errors && errors.length > 0) {
                text += `\n\n${errors.slice(0, 5).join('\n')}`;
            }
            Swal.fire({
                html: text.replace(/\n/g, '<br>'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } catch (error) {
            Swal.fire({
                text: t('patchNotes.importError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setImporting(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const fetchDrafts = useCallback(async () => {
        setDraftsLoading(true);
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/admin/patch-notes/drafts`);
            setDrafts(response.data);
        } catch (error) {
            console.error('Error fetching patch notes drafts:', error);
        } finally {
            setDraftsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'drafts') {
            fetchDrafts();
        }
    }, [activeTab, fetchDrafts]);

    const handleEditDraft = (draft) => {
        const titles = { en: '', ru: '', ua: '', ko: '', fr: '' };
        const contents = { en: '', ru: '', ua: '', ko: '', fr: '' };
        ['en', 'ru', 'ua', 'ko', 'fr'].forEach((lang) => {
            titles[lang] = draft.titles?.[lang] || '';
            contents[lang] = draft.descriptions?.[lang] || '';
        });
        setEditPatchNotesData({
            date: formatDateForInput(draft.created_at),
            title: titles,
            content: contents,
            version: draft.version || ''
        });
        setSelectedGroupId(draft.group_id);
        setSelectedPatchNotes({ image: draft.image, author: draft.author });
        setFileName(draft.image ? draft.image.split('/').pop() : '');
        setMedia(null);
        setShowEditPreview(false);
        setIsDraftEdit(true);
        setIsEditing(true);
        setActiveTab('managePatchNotes');
    };

    const handlePublishDraft = async () => {
        setIsSubmitting(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        const sanitizedContent = {
            en: DOMPurify.sanitize(editPatchNotesData.content.en),
            ru: DOMPurify.sanitize(editPatchNotesData.content.ru),
            ua: DOMPurify.sanitize(editPatchNotesData.content.ua),
            ko: DOMPurify.sanitize(editPatchNotesData.content.ko),
            fr: DOMPurify.sanitize(editPatchNotesData.content.fr),
        };
        try {
            await api.post(`${process.env.REACT_APP_API_URL}/api/admin/patch-notes/${selectedGroupId}/publish`, {
                titles: editPatchNotesData.title,
                contents: sanitizedContent,
                version: editPatchNotesData.version,
                date: editPatchNotesData.date
            });
            Swal.fire({
                text: t('patchNotes.publishSuccess'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            setIsEditing(false);
            setIsDraftEdit(false);
            setSelectedGroupId(null);
            setEditPatchNotesData(null);
            setMedia(null);
            setFileName('');
            setShowEditPreview(false);
        } catch (error) {
            Swal.fire({
                text: t('patchNotes.publishError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setIsSubmitting(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const confirmPublishDraft = (e) => {
        e.preventDefault();
        Swal.fire({
            text: t('patchNotes.confirmSubmit'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handlePublishDraft();
            }
        });
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setIsDraftEdit(false);
        setSelectedGroupId(null);
        setEditPatchNotesData(null);
        setMedia(null);
        setFileName('');
        setShowEditPreview(false);
    };

    const handleDeleteDraft = (groupId) => {
        Swal.fire({
            text: t('patchNotes.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.delete(`${process.env.REACT_APP_API_URL}/api/admin/patch-notes/${groupId}`);
                    setDrafts(drafts.filter(d => d.group_id !== groupId));
                } catch (error) {
                    Swal.fire({
                        text: t('patchNotes.deleteError'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('posts.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                }
            }
        });
    };

    return (
        <div>
            <Tabs
                activeKey={activeTab}
                onSelect={(k) => {
                    setActiveTab(k);
                    if (k === 'createPatchNotes') {
                        setCreatePatchNotesData({
                            date: getCurrentDateTime(),
                            title: { en: '', ru: '', ua: '', ko: '', fr: '' },
                            content: { en: '', ru: '', ua: '', ko: '', fr: '' },
                            version: ''
                        });
                        setFileName('');
                        setMedia(null);
                        setShowPreview(false);
                    } else if (k === 'managePatchNotes' && isEditing && selectedPatchNotes?.image) {
                        setFileName(selectedPatchNotes.image.split('/').pop());
                    }
                }}
                id="patch-notes-management-tabs"
                className="mb-3"
            >
                <Tab eventKey="createPatchNotes" title={t('patchNotes.createPatchNotes')}>
                    {!showPreview ? (
                        <form onSubmit={confirmSubmitPatchNotes}>
                            <div className="form-group">
                                <label>{t('patchNotes.date')}</label>
                                <input
                                    type="datetime-local"
                                    value={createPatchNotesData.date}
                                    onChange={handleDateChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('patchNotes.version')}</label>
                                <input
                                    type="text"
                                    value={createPatchNotesData.version}
                                    onChange={handleVersionChange}
                                    placeholder="v1.0.0"
                                />
                            </div>
                            <Tabs
                                activeKey={language}
                                onSelect={(k) => setLanguage(k)}
                                id="patch-notes-language-tabs"
                                className="mb-3"
                            >
                                {['en', 'ru', 'ua', 'ko', 'fr'].map((lang) => (
                                    <Tab eventKey={lang} title={t(`languages.${lang}`)} key={lang}>
                                        <div className="form-group">
                                            <label>{t('patchNotes.patchNotesTitle')} ({t(`languages.${lang}`)}):</label>
                                            <input
                                                type="text"
                                                value={createPatchNotesData.title[lang]}
                                                onChange={(e) => handleChange('title', e.target.value, lang)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('patchNotes.content')} ({t(`languages.${lang}`)}):</label>
                                            <RichTextEditor
                                                value={createPatchNotesData.content[lang]}
                                                onChange={(value) => handleChange('content', value, lang)}
                                            />
                                        </div>
                                    </Tab>
                                ))}
                            </Tabs>
                            <div>
                                {fileName && <span title={fileName}>{fileName}</span>}
                            </div>
                            <div className="form-buttons">
                                <button type="submit" className="submit-button" disabled={isSubmitting}>
                                    {isSubmitting ? t('patchNotes.adding') : t('admin.create')}
                                </button>
                                <button type="button" className="preview-button" onClick={handleSaveDraft} disabled={isSubmitting}>
                                    {t('patchNotes.saveDraft')}
                                </button>
                                <button type="button" className="preview-button" onClick={handleTranslateCreate} disabled={translating}>
                                    {translating ? t('patchNotes.translating') : t('patchNotes.translate')}
                                </button>
                                <label className="custom-file-upload">
                                    <input type="file" onChange={handleFileChange} />
                                    {t('posts.upload')}
                                </label>
                                <button type="button" className="preview-button" onClick={handlePreview}>
                                    {t('patchNotes.preview')}
                                </button>
                                <button type="button" className="preview-button" onClick={handleImport} disabled={importing}>
                                    {importing ? t('patchNotes.importing') : t('patchNotes.import')}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="preview-section">
                            <PatchNotesItem
                                item={{
                                    title: createPatchNotesData.title[language],
                                    description: createPatchNotesData.content[language],
                                    created_at: createPatchNotesData.date,
                                    author: 'admin',
                                    version: createPatchNotesData.version,
                                    id: 'preview'
                                }}
                                background={previewImageUrl || backgrounds[Math.floor(Math.random() * backgrounds.length)]}
                                isPreview={true}
                            />
                            <button type="button" className="back-button" onClick={handleBackToEditing}>
                                {t('patchNotes.backToEditing')}
                            </button>
                        </div>
                    )}
                </Tab>
                <Tab eventKey="managePatchNotes" title={t('patchNotes.managePatchNotes')}>
                    {isEditing ? (
                        !showEditPreview ? (
                            <form onSubmit={isDraftEdit ? confirmPublishDraft : confirmEditPatchNotes}>
                                {selectedPatchNotes && selectedPatchNotes.image && (
                                    <div className="patch-notes-content" style={{justifyContent: 'center'}}>
                                        <div>
                                            <img src={selectedPatchNotes.image} alt="Current patch notes image"
                                                 style={{maxWidth: '400px'}}/>
                                        </div>
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>{t('patchNotes.date')}</label>
                                    <input
                                        type="datetime-local"
                                        value={editPatchNotesData ? editPatchNotesData.date : ''}
                                        onChange={handleDateChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('patchNotes.version')}</label>
                                    <input
                                        type="text"
                                        value={editPatchNotesData ? editPatchNotesData.version : ''}
                                        onChange={handleVersionChange}
                                        placeholder="v1.0.0"
                                    />
                                </div>
                                <Tabs
                                    activeKey={language}
                                    onSelect={(k) => setLanguage(k)}
                                    id="patch-notes-language-tabs"
                                    className="mb-3"
                                >
                                    {['en', 'ru', 'ua', 'ko', 'fr'].map((lang) => (
                                        <Tab eventKey={lang} title={t(`languages.${lang}`)} key={lang}>
                                            <div className="form-group">
                                                <label>{t('patchNotes.patchNotesTitle')} ({t(`languages.${lang}`)}):</label>
                                                <input
                                                    type="text"
                                                    value={editPatchNotesData ? editPatchNotesData.title[lang] : ''}
                                                    onChange={(e) => handleChange('title', e.target.value, lang)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>{t('patchNotes.content')} ({t(`languages.${lang}`)}):</label>
                                                <RichTextEditor
                                                    value={editPatchNotesData ? editPatchNotesData.content[lang] : ''}
                                                    onChange={(value) => handleChange('content', value, lang)}
                                                />
                                            </div>
                                        </Tab>
                                    ))}
                                </Tabs>
                                <div>
                                    {fileName && <span title={fileName}>{fileName}</span>}
                                </div>
                                <div className="form-buttons">
                                    {isDraftEdit ? (
                                        <>
                                            <button type="button" className="preview-button" onClick={handleTranslate} disabled={translating}>
                                                {translating ? t('patchNotes.translating') : t('patchNotes.translate')}
                                            </button>
                                            <button type="submit" className="submit-button" disabled={isSubmitting}>
                                                {isSubmitting ? t('patchNotes.publishing') : t('patchNotes.publish')}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button type="submit" className="submit-button" disabled={isSubmitting}>
                                                {isSubmitting ? t('patchNotes.editing') : t('patchNotes.EditPatchNotes')}
                                            </button>
                                            <button type="button" className="preview-button" onClick={handleTranslate} disabled={translating}>
                                                {translating ? t('patchNotes.translating') : t('patchNotes.translate')}
                                            </button>
                                            <label className="custom-file-upload">
                                                <input type="file" onChange={handleFileChange}/>
                                                {t('posts.upload')}
                                            </label>
                                        </>
                                    )}
                                    <button type="button" className="preview-button" onClick={() => setShowEditPreview(true)}>
                                        {t('patchNotes.preview')}
                                    </button>
                                    <button type="button" className="edit-button" onClick={cancelEdit}>
                                        {t('patchNotes.cancelEditing')}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="preview-section">
                                <h3 className="preview-label">{t('patchNotes.previewCard')}</h3>
                                <PatchNotesItem
                                    item={{
                                        title: editPatchNotesData.title[language],
                                        description: editPatchNotesData.content[language],
                                        created_at: editPatchNotesData.date,
                                        author: selectedPatchNotes ? selectedPatchNotes.author : 'admin',
                                        version: editPatchNotesData.version,
                                        id: 'preview'
                                    }}
                                    background={previewImageUrl || (selectedPatchNotes && selectedPatchNotes.image) || backgrounds[Math.floor(Math.random() * backgrounds.length)]}
                                    isPreview={true}
                                />
                                <h3 className="preview-label">{t('patchNotes.previewDetail')}</h3>
                                <div className="news-detail-container">
                                    <div className="news-detail-banner">
                                        <div
                                            className="banner-background"
                                            style={{
                                                backgroundImage: `url(${previewImageUrl || (selectedPatchNotes && selectedPatchNotes.image) || ''})`
                                            }}
                                        ></div>
                                        <div className="banner-content">
                                            <div className="banner-category">{editPatchNotesData.version || t('patchNotes.title')}</div>
                                            <h1 className="banner-title">{editPatchNotesData.title[language]}</h1>
                                            <div className="banner-meta">
                                                <span><FontAwesomeIcon icon={faCalendarAlt}/> {editPatchNotesData.date}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <main className="news-detail-main">
                                        <div className="news-detail-body">
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(editPatchNotesData.content[language])
                                                }}
                                            />
                                        </div>
                                    </main>
                                </div>
                                <button type="button" className="back-button" onClick={() => setShowEditPreview(false)}>
                                    {t('patchNotes.backToEditing')}
                                </button>
                            </div>
                        )
                    ) : (
                        <div className="patch-notes-list">
                            {isLoading ? (
                                <div className="text-center">
                                    <LoadingSpinner/>
                                    <p>{t('patchNotes.loading')}</p>
                                </div>
                            ) : patchNotesList.length === 0 ? (
                                <p>{t('patchNotes.noPatchNotes')}</p>
                            ) : (
                                <>
                                    <div id="patch-notes-container-admin-page">
                                        {patchNotesList.map((item, index) => {
                                            const background = item.image ? item.image : randomBackgrounds[index % randomBackgrounds.length];
                                            return (
                                                <PatchNotesItem
                                                    key={item.id}
                                                    item={item}
                                                    background={background}
                                                    confirmDeletePatchNotes={confirmDeletePatchNotes}
                                                    handleSelectPatchNotes={() => handleSelectPatchNotes(item.group_id)}
                                                />
                                            );
                                        })}
                                    </div>
                                    {totalPages > 1 && (
                                        <ReactPaginate
                                            breakLabel="..."
                                            nextLabel={null}
                                            onPageChange={handlePageClick}
                                            pageRangeDisplayed={1}
                                            marginPagesDisplayed={2}
                                            pageCount={totalPages}
                                            previousLabel={null}
                                            forcePage={page - 1}
                                            containerClassName="pagination-container"
                                            pageClassName="pagination-item"
                                            pageLinkClassName="pagination-link"
                                            previousClassName="pagination-item"
                                            nextClassName="pagination-next"
                                            previousLinkClassName="pagination-previous"
                                            nextLinkClassName="pagination-link"
                                            breakClassName="pagination-item"
                                            breakLinkClassName="pagination-link"
                                            activeClassName="active"
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Tab>
                <Tab eventKey="drafts" title={t('patchNotes.drafts')}>
                    <div className="news-list">
                        {draftsLoading ? (
                            <div className="text-center">
                                <LoadingSpinner/>
                                <p>{t('patchNotes.loading')}</p>
                            </div>
                        ) : drafts.length === 0 ? (
                            <p>{t('patchNotes.noDrafts')}</p>
                        ) : (
                            drafts.map((draft) => {
                                const title = draft.titles[i18n.language] || draft.titles.en || draft.titles.ru || '';
                                return (
                                    <div className="draft-item" key={draft.group_id}>
                                        {draft.image && <img src={draft.image} alt="" className="draft-thumbnail"/>}
                                        <div className="draft-info">
                                            <span className="draft-title">{title}</span>
                                            <span className="draft-meta">{[draft.source, draft.created_at, draft.author].filter(Boolean).join(' · ')}</span>
                                        </div>
                                        <div className="draft-actions">
                                            <button
                                                className="submit-button"
                                                onClick={() => handleEditDraft(draft)}
                                            >
                                                {t('patchNotes.edit')}
                                            </button>
                                            <button
                                                className="preview-button"
                                                onClick={() => handleDeleteDraft(draft.group_id)}
                                            >
                                                {t('patchNotes.delete')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </Tab>
            </Tabs>
        </div>
    );
}

const PatchNotesItem = ({item, background, confirmDeletePatchNotes, handleSelectPatchNotes, isPreview}) => {
    const contentRef = React.useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);
    const {t} = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        const current = contentRef.current;
        if (current) {
            const observer = new ResizeObserver(() => {
                setIsOverflowed(current.scrollHeight > current.clientHeight);
            });
            observer.observe(current);
            setIsOverflowed(current.scrollHeight > current.clientHeight);

            return () => {
                observer.disconnect();
            };
        }
    }, [item.description]);

    const formatDate = (createdAt) => {
        if (!createdAt) return '';
        const date = new Date(createdAt);
        if (isNaN(date.getTime())) return '';

        const localeMap = {
            'ua': 'uk',
            'en': 'en',
            'ko': 'ko',
            'fr': 'fr',
            'ru': 'ru',
        };
        const locale = localeMap[i18n.language] || 'en';

        return date.toLocaleString(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getPluralForm = (count, singular, few, many) => {
        const mod10 = count % 10;
        const mod100 = count % 100;

        if (mod10 === 1 && mod100 !== 11) {
            return singular;
        }
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
            return few;
        }
        return many;
    };

    const calculateReadTime = (text) => {
        if (!text) return `${t('patchNotes.readTime', { count: 1 })}`;
        const words = text.split(/\s+/).length;
        const readTime = Math.ceil(words / 200);
        const singular = t('patchNotes.readTimeSingular');
        const few = t('patchNotes.readTimeFew');
        const many = t('patchNotes.readTimeMany');
        const pluralForm = getPluralForm(readTime, singular, few, many);
        return `${readTime} ${pluralForm} ${t('patchNotes.forReading')}`;
    };

    const readMore = () => {
        navigate(`/patch-notes/${item.id}`);
    };

    return (
        <article className="news-item">
            <div className="news-content">
                <div className="news-thumbnail">
                    <img src={background} alt="patch notes thumbnail" />
                </div>
                <div className="news-data">
                    <div className="news-meta">
                        <span><FontAwesomeIcon icon={faCalendarAlt}/> {formatDate(item.createdAt?.date || item.created_at)}</span>
                        <span><FontAwesomeIcon icon={faUser}/> {item.author || 'admin'}</span>
                        {item.version && (
                            <span className="version-badge">
                                <FontAwesomeIcon icon={faTag}/> {item.version}
                            </span>
                        )}
                        <span><FontAwesomeIcon icon={faClock}/> {calculateReadTime(item.description)}</span>
                        {!isPreview && (
                            <>
                                <span>
                                    <button
                                        className="patch-notes-edit-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectPatchNotes();
                                        }}
                                        aria-label={t('patchNotes.edit')}
                                    >
                                        <FontAwesomeIcon icon={faEdit}/> {t('patchNotes.edit')}
                                    </button>
                                </span>
                                <span>
                                    <button
                                        className="patch-notes-delete-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            confirmDeletePatchNotes(item.group_id);
                                        }}
                                        aria-label={t('patchNotes.delete')}
                                    >
                                        <FontAwesomeIcon icon={faTrash}/> {t('patchNotes.delete')}
                                    </button>
                                </span>
                            </>
                        )}
                        {!isPreview && isOverflowed && (
                            <span className="read-more" onClick={readMore}>
                                <FontAwesomeIcon icon={faBookOpen}/> {t('patchNotes.readMore') || 'Read More'}
                                <FontAwesomeIcon className="fa-arrow-right" icon={faArrowRight}/>
                            </span>
                        )}
                    </div>
                    {!isPreview ? (
                        <span className="news-title">
                            <Link to={`/patch-notes/${item.id}`}>{item.title}</Link>
                        </span>
                    ) : (
                        <h2 className="news-title">{item.title}</h2>
                    )}
                    <div
                        ref={contentRef}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(item.description, {FORBID_TAGS: ['img']})
                        }}
                        className="news-description"
                    ></div>
                </div>
            </div>
        </article>
    );
};

export default PatchNotesTab;

